import React, { useState, useEffect, useMemo } from 'react';

import ReportLayout, {
  StatusBadge
} from './components/ReportLayout';
import {
  Package,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  TrendingDown,
  ShoppingCart,
  Boxes,
  Building2,
  ChevronDown
} from 'lucide-react';

import useStockStatus from '@/hooks/useStockStatus';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import { inventoryitemService } from "../../../services/inventoryitemService";

// ─── Period filter (mirrors the Stock Consumption report) ───────────────────
const PERIOD_OPTIONS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'thisWeek',  label: 'This Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
];

// Preset key → backend period enum. Manual date edits use DATE_RANGE.
const PERIOD_ENUM = {
  today:     'TODAY',
  yesterday: 'YESTERDAY',
  thisWeek:  'THIS_WEEK',
  thisMonth: 'THIS_MONTH',
  lastMonth: 'LAST_MONTH',
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Returns { startDate, endDate } as 'YYYY-MM-DD' strings for the given preset.
// Used only to keep the date inputs in sync with the selected preset; the
// backend resolves preset ranges itself from the period enum.
const getPeriodRange = (period) => {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (period) {
    case 'today': {
      const d = startOfDay(now);
      return { startDate: toYMD(d), endDate: toYMD(d) };
    }
    case 'yesterday': {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 1);
      return { startDate: toYMD(d), endDate: toYMD(d) };
    }
    case 'thisWeek': {
      const d = startOfDay(now);
      const diffToMonday = (d.getDay() + 6) % 7;
      const start = new Date(d);
      start.setDate(d.getDate() - diffToMonday);
      return { startDate: toYMD(start), endDate: toYMD(d) };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: toYMD(start), endDate: toYMD(startOfDay(now)) };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end   = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: toYMD(start), endDate: toYMD(end) };
    }
    default:
      return { startDate: null, endDate: null };
  }
};

export default function StockStatusReport() {
  const { hasPermission } = useAuth();

if (!hasPermission(P.REPORTS.INVENTORY.SCREEN)) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>
        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}


 const [categories, setCategories] = useState([]);

 useEffect(() => {
   const load = async () => {
     const result = await inventoryitemService.getAllCategories();
     setCategories(result || []);
   };

   load();
 }, []);

  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map(c => `"${c.label}"`).join(',');
    const rows = data.map(r =>
      columns.map(c => `"${r[c.key] ?? ''}"`).join(',')
    );

    const blob = new Blob([headers, '\n', rows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Stock-Status-report.csv';
    link.click();
  };




  const [selectedCategory, setSelectedCategory] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');


  const [sortColumn, setSortColumn] = useState('stockStatus');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');


  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ── Period filter state ────────────────────────────────────────────────
  // activePreset  → which pill is highlighted (null when custom range).
  // draftRange    → what the two date inputs display. Editing it does NOT
  //                 fetch; only the period Apply button commits it.
  // The APPLIED period lives inside the hook's `filters` (period/startDate/
  // endDate) — the hook refetches automatically whenever filters changes,
  // which is exactly the auto-apply behaviour presets need.
  const initialPeriodRange = getPeriodRange('today');
  const [activePreset, setActivePreset] = useState('today');
  const [draftRange, setDraftRange] = useState({
    from: initialPeriodRange.startDate,
    to:   initialPeriodRange.endDate,
  });

  const {
    data = [],
    summary,
    branchSummary,
    loading,
    error,
    filters,
    setFilters,
    refresh
  } = useStockStatus({
    categoryId: '',
    stockStatus: 'all',
    sortBy: 'name',
    offset: 0,
    limit: pageSize,
    period: 'TODAY',        // matches backend default; new params flow through
    startDate: undefined,
    endDate: undefined
  });

  // Preset click → auto-applies: writing period into filters triggers the
  // hook's refetch. Dates are sent as undefined (backend resolves the enum).
  const applyPeriodPreset = (key) => {
    const { startDate, endDate } = getPeriodRange(key);
    setActivePreset(key);
    setDraftRange({ from: startDate, to: endDate });
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      period: PERIOD_ENUM[key],
      startDate: undefined,
      endDate: undefined,
      offset: 0
    }));
  };

  // Manual date edit → draft only. Clears the pill highlight, no fetch.
  // The draft is committed by the single "Apply Filters" button below.
  const handleDraftChange = (field, value) => {
    setDraftRange((r) => ({ ...r, [field]: value }));
    setActivePreset(null);
  };

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

const handleSort = (column) => {
  setSortDirection(prev =>
    sortColumn === column
      ? (prev === 'asc' ? 'desc' : 'asc')
      : 'asc'
  );
  setSortColumn(column);
};


  const handleSearch = (query) => {
    setCurrentPage(1);
  };

  const handleExport = (format) => {
    console.log(`Exporting stock status as ${format}...`);
  };

  const handleRefresh = () => {

    setCurrentPage(1);

    // ONE Apply button for everything: commits Category, Stock Status, Sort,
    // and — when the user has edited the dates (no preset highlighted) — the
    // custom Date Range as period='DATE_RANGE' + startDate/endDate.
    // When a preset is highlighted, the preset's period enum is kept and the
    // explicit dates are cleared so the API receives ONLY `period`.
    // setFilters alone triggers the hook's fetch; calling refresh() as well
    // would fire a second request with stale filters, so it's not called.
    setFilters(prev => {
      const next = {
        ...prev,
        categoryId: selectedCategory || '',
        stockStatus: stockFilter || 'all',
        sortBy: sortBy || 'name',
        offset: 0,
        limit: pageSize
      };

      if (activePreset) {
        next.period = PERIOD_ENUM[activePreset];
        next.startDate = undefined;   // predefined period → period param only
        next.endDate = undefined;
      } else if (draftRange.from && draftRange.to) {
        next.period = 'DATE_RANGE';   // custom range → dates only (+ marker)
        next.startDate = draftRange.from;
        next.endDate = draftRange.to;
      }

      return next;
    });
  };



  const computedCategories = [
    { id: '', name: 'All Categories' },
    ...(categories || [])
  ];


  const summaryCards = [
    {
      title: 'Total Items',
      value: (summary?.totalItems || 0).toLocaleString(),
      subtitle: 'Inventory items',
      icon: Package
    },
    {
      title: 'In Stock',
      value: (summary?.itemsInStock || 0).toLocaleString(),
      subtitle: 'Items OK',
      icon: CheckCircle2
    },
    {
      title: 'Low Stock',
      value: (summary?.lowStockItems || 0).toLocaleString(),
      subtitle: 'Needs reorder',
      icon: AlertTriangle
    },
    {
      title: 'Out of Stock',
      value: (summary?.outOfStockItems || 0).toLocaleString(),
      subtitle: 'Critical items',
      icon: AlertCircle
    },
    {
      title: 'Stock Value',
      value: `₹${(summary?.totalStockValue || 0).toLocaleString()}`,
      subtitle: 'Total inventory',
      icon: DollarSign
    },
    {
      title: 'At Risk Value',
      value: `₹${(summary?.lowStockValue || 0).toLocaleString()}`,
      subtitle: 'Low stock items',
      icon: TrendingDown
    }
  ];


  const filtersUI = (
    <div className="flex flex-col gap-4">
      {/* Row 1 — Period / Date Range. Presets apply automatically; the custom
          date range is committed by the single "Apply Filters" button below. */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Period</label>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPeriodPreset(p.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                  activePreset === p.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <input
            type="date"
            value={draftRange.from}
            max={draftRange.to || undefined}
            onChange={(e) => handleDraftChange('from', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={draftRange.to}
            min={draftRange.from || undefined}
            onChange={(e) => handleDraftChange('to', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Row 2 — Category / Stock Status / Sort, applied together with the
          custom Date Range by the single Apply Filters button. */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Category</label>
          <select
            value={selectedCategory}
           onChange={(e) => {
             setSelectedCategory(e.target.value);
           }}

            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-w-[180px]"
          >
            {computedCategories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Stock Status</label>
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
            {[
              { value: 'all', label: 'All', icon: Boxes },
              { value: 'low', label: 'Low Stock', icon: AlertTriangle },
              { value: 'out_of_stock', label: 'Out of Stock', icon: AlertCircle },
            ].map((option) => (
              <button
                key={option.value}
               onClick={() => {
                 setStockFilter(option.value);
               }}

                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                  stockFilter === option.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <option.icon size={12} />
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="name">Name</option>
            <option value="stock_level">Stock Level</option>
            <option value="value">Stock Value</option>
          </select>

        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-transparent">Apply</label>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            Apply Filters
          </button>
        </div>

        {branchOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Branch</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => {
                  const val = e.target.value || null;
                  setSelectedBranchId(val);
                  setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
                }}
                className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branchOptions.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </div>
  );


  const pagination = {
    currentPage,
    totalPages: Math.ceil((summary?.totalItems || 0) / pageSize),
    totalItems: summary?.totalItems || 0,
    pageSize,
    onPageChange: (page) => {
      setCurrentPage(page);
      setFilters(prev => ({
        ...prev,
        offset: (page - 1) * pageSize
      }));
    },
    onPageSizeChange: (size) => {
      setPageSize(size);
      setCurrentPage(1);
      setFilters(prev => ({
        ...prev,
        limit: size,
        offset: 0
      }));
    }
  };


  const columns = [
//     {
//       key: 'itemCode',
//       label: 'Code',
//       sortable: true,
//       render: (value) => (
//         <span className="font-mono text-xs text-gray-500">{value}</span>
//       )
//     },
    {
      key: 'itemName',
      label: 'Item',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <StatusBadge status={row.categoryName} type="info" />
        </div>
      )
    },
    // ── Period analytics (from the selected Period filter) ────────────────
    {
      key: 'openingStock',
      label: 'Opening Stock',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <span className="text-gray-600">{value ?? 0} {row.unit}</span>
      )
    },
    {
      key: 'purchase',
      label: 'Purchase',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <span className={`font-medium ${Number(value) > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
          {Number(value) > 0 ? '+' : ''}{value ?? 0} {row.unit}
        </span>
      )
    },
    {
      key: 'sale',
      label: 'Sale',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <span className={`font-medium ${Number(value) > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
          {Number(value) > 0 ? '−' : ''}{value ?? 0} {row.unit}
        </span>
      )
    },
    {
      key: 'remainingStock',
      label: 'Remaining Stock',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <span className={`font-semibold ${Number(value) <= 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {value ?? 0} {row.unit}
        </span>
      )
    },
    // ── Live inventory state (unchanged) ───────────────────────────────────
    {
      key: 'currentStock',
      label: 'Current Stock',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <div className="text-right">
          <span className={`font-semibold ${
            row.stockStatus === 'out_of_stock' ? 'text-red-600' :
            row.stockStatus === 'low' ? 'text-amber-600' : 'text-green-600'
          }`}>
            {value} {row.unit}
          </span>
        </div>
      )
    },
    {
      key: 'reorderLevel',
      label: 'Reorder Level',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <span className="text-gray-600">{value} {row.unit}</span>
      )
    },
    {
      key: 'reorderQuantity',
      label: 'Reorder Qty',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <span className="text-gray-600">{value} {row.unit}</span>
      )
    },
    {
      key: 'stockStatus',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (value) => {
        const statusConfig = {
          ok: { label: 'In Stock', type: 'success', icon: CheckCircle2 },
          low: { label: 'Low Stock', type: 'warning', icon: AlertTriangle },
          out_of_stock: { label: 'Out of Stock', type: 'danger', icon: AlertCircle },
        };
        const config = statusConfig[value];
        const Icon = config.icon;
        return (
          <div className="flex items-center justify-center gap-1">
            <Icon size={14} className={
              value === 'ok' ? 'text-green-600' :
              value === 'low' ? 'text-amber-600' : 'text-red-600'
            } />
            <StatusBadge status={config.label} type={config.type} />
          </div>
        );
      }
    },
    {
      key: 'unitCost',
      label: 'Unit Cost',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="text-gray-600">₹{value?.toLocaleString?.() || 0}</span>
      )
    },
    {
      key: 'stockValue',
      label: 'Stock Value',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="font-semibold text-gray-900">₹{value?.toLocaleString?.() || 0}</span>
      )
    },
    {
      key: 'lastPurchaseDate',
      label: 'Last Purchase',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm">
          <p className="text-gray-900">
            {value ? new Date(value).toLocaleDateString('en-IN') : '-'}
          </p>
          <p className="text-xs text-gray-500">
            {row.lastPurchaseQty} {row.unit}
          </p>
        </div>
      )
    }
  ];

//SORTING
const sortedData = useMemo(() => {
  if (!data?.length || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (sortColumn === 'lastPurchaseDate') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in item name
    if (String(row.itemName || '').toLowerCase().includes(query)) return true;
    // Search in category name
    if (String(row.categoryName || '').toLowerCase().includes(query)) return true;
    // Search in unit
    if (String(row.unit || '').toLowerCase().includes(query)) return true;
    // Search in stock status
    if (String(row.stockStatus || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['currentStock', 'reorderLevel', 'reorderQuantity', 'unitCost', 'stockValue', 'openingStock', 'purchase', 'sale', 'remainingStock'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Stock Status Report"
      description="Current inventory levels with low stock alerts for reorder planning"
      icon={Package}
      exportPermission={P.REPORTS.INVENTORY.STOCK_EXPORT}
      color="amber"
      category="Inventory"
      categoryId="inventory"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filtersUI}
      loading={loading}
      error={error}
      pagination={pagination}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by item name, category..."
      onExport={(format) => {
              if (format === 'csv') exportToCsv();
            }}
      onRefresh={handleRefresh}
    >

      {/* Quick Actions & Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

       {/* Critical Items */}
       <div className="bg-white border border-red-200 rounded-lg p-4">
         <div className="flex items-center gap-2 mb-4">
           <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
             <AlertCircle size={16} className="text-red-600" />
           </div>
           <div>
             <h3 className="font-semibold text-gray-900">Out of Stock</h3>
             <p className="text-xs text-gray-500">Requires immediate action</p>
           </div>
         </div>

         {/* 🔥 SCROLL ENABLED HERE */}
         <div className="space-y-2 max-h-[200px] overflow-y-auto">

           {(data || []).filter(i => i.stockStatus === 'out_of_stock').map((item) => (
             <div
               key={item.itemId}
               className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100"
             >
               <div>
                 <p className="text-sm font-medium text-gray-900">{item.itemName}</p>
                 <p className="text-xs text-gray-500">
                   {item.categoryName} • Reorder: {item.reorderQuantity} {item.unit}
                 </p>
               </div>

               <button className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1">
                 <ShoppingCart size={12} />
                 Order Now
               </button>
             </div>
           ))}

           {(data || []).filter(i => i.stockStatus === 'out_of_stock').length === 0 && (
             <div className="text-center py-4 text-gray-500 text-sm">
               <CheckCircle2 size={24} className="mx-auto mb-2 text-green-500" />
               No items out of stock
             </div>
           )}
         </div>
       </div>


        {/* Low Stock Items */}
        <div className="bg-white border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={16} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Low Stock Alert</h3>
              <p className="text-xs text-gray-500">Plan reorder soon</p>
            </div>
          </div>

          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {(data || []).filter(i => i.stockStatus === 'low').map((item) => (
              <div
                key={item.itemId}
                className="flex items-center justify-between p-2 bg-amber-50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.itemName}</p>
                  <p className="text-xs text-gray-500">
                    {item.currentStock}/{item.reorderLevel} {item.unit}
                  </p>
                </div>

                <span className="text-sm font-semibold text-amber-600">
                  Need {Number(item.reorderLevel - item.currentStock || 0).toFixed(2)} {item.unit}
                </span>
              </div>
            ))}
          </div>

        </div>

      </div>

    </ReportLayout>
  );
}